import type { LocalDate, LocalTime } from '../../core/util/datetime';
import { isValidDate, isValidTime, nowTime, plusMinutes, today } from '../../core/util/datetime';
import { DEFAULT_LOCATION, EMPTY_FILTERS } from '../../core/model/search';
import type { LocationOption, SearchCriteria, SearchFilters, SortOption } from '../../core/model/search';
import { readJson, storageKey, writeJson } from './storage';

const KEY = storageKey('search-criteria');

/**
 * Criterios de búsqueda persistidos entre sesiones.
 *
 * Equivale al `SearchCriteriaStore` sobre DataStore del cliente Android: el usuario que vuelve
 * encuentra su zona y su número de personas, no un formulario en blanco. La fecha y la hora
 * **no** se restauran si ya pasaron, porque restaurar un horario vencido produce una búsqueda
 * que no puede dar resultados.
 */
interface PersistedCriteria {
  query?: string;
  location?: LocationOption;
  date?: string;
  time?: string;
  partySize?: number;
  filters?: SearchFilters;
  sort?: SortOption;
}

/** Hora por defecto: la próxima media hora en punto, que es cuando la gente busca mesa. */
export function defaultTime(now: Date = new Date()): LocalTime {
  const current = nowTime(now);
  const [hours, minutes] = current.split(':').map(Number);
  const rounded = minutes === 0 || minutes === 30 ? 0 : minutes < 30 ? 30 - minutes : 60 - minutes;
  return plusMinutes(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`, rounded + 60);
}

export function defaultCriteria(now: Date = new Date()): SearchCriteria {
  return {
    query: '',
    location: DEFAULT_LOCATION,
    date: today(now),
    time: defaultTime(now),
    partySize: 2,
    filters: EMPTY_FILTERS,
    sort: 'RELEVANCE',
  };
}

export function loadCriteria(now: Date = new Date()): SearchCriteria {
  const fallback = defaultCriteria(now);
  const stored = readJson<PersistedCriteria>(KEY);
  if (!stored) return fallback;

  const storedDate: LocalDate | null =
    isValidDate(stored.date) && stored.date >= fallback.date ? stored.date : null;

  return {
    query: '',
    location: stored.location ?? fallback.location,
    date: storedDate ?? fallback.date,
    // La hora solo se conserva junto con una fecha aún válida; si la fecha caducó, conservar
    // la hora dejaría un criterio a medias.
    time: storedDate && isValidTime(stored.time) ? stored.time : fallback.time,
    partySize: stored.partySize && stored.partySize > 0 ? stored.partySize : fallback.partySize,
    filters: stored.filters ?? fallback.filters,
    sort: stored.sort ?? fallback.sort,
  };
}

export function saveCriteria(criteria: SearchCriteria): void {
  // La consulta de texto no se persiste: es de un momento concreto, no una preferencia.
  writeJson(KEY, {
    location: criteria.location,
    date: criteria.date,
    time: criteria.time,
    partySize: criteria.partySize,
    filters: criteria.filters,
    sort: criteria.sort,
  } satisfies PersistedCriteria);
}
